<template>
  <div class="promo-block" data-qa="basket-promo-container" @click.stop>
    <div v-if="applied" class="promo-applied" data-qa="basket-promo-applied">
      <div>
        <b>{{ code }}</b
        >{{ ' ' + $t('promo_code_applied') }}
      </div>
      <button
        class="link"
        type="button"
        data-qa="basket-promo-remove-btn"
        @click="$emit('remove')"
      >
        {{ $t('promo_code_remove') }}
      </button>
    </div>
    <template v-else>
      <div
        v-if="latinOnlyError"
        class="latinOnly_block flex justify-start basket-row col-span-6"
      >
        <div class="icon mr-1">
          <ExclamationAlertIcon color="#ff585d" topPosition="4" />
        </div>
        <p class="text-accentColor">{{ $t('latin_only_russian_error') }}</p>
      </div>
      <form
        class="promo-field"
        :class="{ error: !!error }"
        @submit.prevent="onSubmit"
      >
        <input
          :value="code"
          :placeholder="$t('ORDER_PROMO_CODE_TITLE')"
          :aria-label="$t('ORDER_PROMO_CODE_TITLE')"
          :aria-invalid="error ? 'true' : null"
          :aria-describedby="errorText ? errorId : null"
          data-qa="basket-promo-input"
          @input="$emit('input', $event)"
        />
        <button
          class="apply link"
          type="submit"
          :disabled="!code"
          data-qa="basket-promo-apply-btn"
        >
          {{ $t('apply') }}
        </button>
      </form>
      <div
        v-if="errorText"
        class="error-message"
        :id="errorId"
        data-qa="basket-promo-error"
      >
        {{ errorText }}
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, useId } from 'vue'

const props = defineProps({
  code: {
    type: String,
    default: '',
  },
  applied: {
    type: Boolean,
    default: false,
  },
  error: {
    type: [String, Boolean, Object],
    default: '',
  },
  latinOnlyError: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['input', 'apply', 'remove'])

const instance = getCurrentInstance()

const errorId = 'basket-promo-error-' + useId()

const errorText = computed(() => {
  if (typeof props.error === 'string' && props.error.trim()) {
    return props.error
  }
  return props.error ? instance.proxy.$t('promo_code_invalid') : ''
})

const onSubmit = () => {
  if (props.code) {
    emit('apply')
  }
}
</script>

<style scoped lang="scss">
.promo-field,
.promo-applied {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  height: 44px;
  border: 2px solid #d1d8dd;
  border-radius: 8px;
  background: transparent;
  transition: border-color 0.15s ease;
}

.promo-applied {
  gap: 12px;
  padding-left: 12px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: -0.014em;
  color: #39444c;
  > div {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  b {
    font-weight: 700;
  }
}

.promo-field {
  &:focus-within {
    border-color: #39444c;
  }
  &.error {
    border-color: #fa5f55;
  }
  input {
    flex: 1;
    box-sizing: border-box;
    min-width: 0;
    height: 100%;
    padding: 10px 12px;
    border: 0;
    border-radius: 0;
    outline: none;
    box-shadow: none;
    background: transparent;
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
    letter-spacing: -0.014em;
    color: #39444c;
    &::placeholder {
      color: #8e9aa4;
    }
  }
}

.link {
  flex-shrink: 0;
  padding-right: 12px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: -0.014em;
  color: rgba(9, 22, 32, 0.8);
  text-decoration: underline;
  outline: none;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.error-message {
  margin-top: 0.5rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: -0.014em;
  color: #fa5f55;
}
</style>
